// types are loose to fit your existing OrganizationTableItem shape
type DealLike = {
  PK: string; // org PK
  onboardingStatus?: string | null; // status on the deal
  // dates may exist in either shape in your data:
  dateRange?: [string | undefined, string | undefined];
  startDate?: string;
  endDate?: string;
  // optional extra flags if you later want to refine "active"
  // status?: string;
};

type OrgLike = {
  PK: string;
  // ...whatever else you have
};

const ONBOARDING_STATUSES = [
  'Staging',
  'Not Started',
  'Bypass',
  'Pending',
  'Awaiting Kickoff',
  'Awaiting Dependencies',
  'Dependencies Received',
  'Launched',
] as const;

const PRE_LAUNCH = ONBOARDING_STATUSES.filter(s => s !== 'Launched');
const PRIORITY = new Map(PRE_LAUNCH.map((s, i) => [s, i] as const));

const coerceDate = (iso?: string) => {
  if (!iso) return undefined;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? undefined : d;
};

// "Active" = now is within [start, end] window if present.
// If only one side is present, we respect that side.
// If neither present, we assume active (so you can refine later if needed).
const isDealActive = (d: DealLike, now = new Date()) => {
  const [startS, endS] = d.dateRange ?? [d.startDate, d.endDate];
  const start = coerceDate(startS);
  const end = coerceDate(endS);

  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
};

// Pick a single org-level status given all active related deal statuses.
// Rule of thumb:
// - if ANY pre-launch exists, org is "Onboarding" with the highest-priority pre-launch status
// - else if only "Launched" exists, org is "Completed" with "Launched"
// - else undefined (no onboarding)
const pickOrgStatus = (statuses: string[]) => {
  const valid = statuses.filter(s => ONBOARDING_STATUSES.includes(s as any)) as typeof ONBOARDING_STATUSES[number][];
  if (!valid.length) return undefined;

  const pre = valid.filter(s => s !== 'Launched');
  if (pre.length) {
    // choose the earliest/highest priority stage by our array order
    return pre.sort((a, b) => (PRIORITY.get(a) ?? 999) - (PRIORITY.get(b) ?? 999))[0];
  }
  if (valid.includes('Launched')) return 'Launched';
  return undefined;
};

export const analyzeOnboarding = <O extends OrgLike, D extends DealLike>(
  organizations: O[],
  deals: D[],
  opts?: { now?: Date; onlyActive?: boolean }
) => {
  const now = opts?.now ?? new Date();
  const onlyActive = opts?.onlyActive ?? true;

  // 1) group deals by org PK (fast lookups)
  const byOrg = deals.reduce<Record<string, D[]>>((acc, d) => {
    (acc[d.PK] ??= []).push(d);
    return acc;
  }, {});

  // 2) transform orgs
  return organizations.map(org => {
    const related = byOrg[org.PK] ?? [];

    const consideredDeals = onlyActive ? related.filter(d => isDealActive(d, now)) : related;

    // Support both onboardingStatus and the occasional "onbaordingStatus" typo defensively
    const statuses = consideredDeals
      .map(d => (d as any).onboardingStatus ?? (d as any).onbaordingStatus)
      .filter(Boolean) as string[];

    const chosen = pickOrgStatus(statuses);

    const isOnboardingCompleted = chosen === 'Launched';
    const isOnboarding = !!chosen && !isOnboardingCompleted;

    return {
      ...org,
      isOnboarding,
      isOnboardingCompleted,
      onboardingStatus: chosen ?? null,

      // extras you might find handy in the UI/debugging:
      _onboardingActiveDealCount: consideredDeals.length,
      _onboardingRelatedDealIds: consideredDeals.map((d: any) => d?.SK || d?.deal_uuid).filter(Boolean),
    };
  });
};
